import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { reviewApi, type ReviewDetail } from "../../../api/clock";
import { Button, ErrorText, TextInput } from "../../../components/ui";

type Action = "approve-in" | "approve-out" | "dismiss" | "session-out" | "apply" | "decline" | "clarify";

function actionsFor(d: ReviewDetail): Action[] {
  if (d.kind === "CORRECTION") return d.item.status === "PENDING" ? ["apply", "decline", "clarify"] : d.item.status === "NEEDS_CLARIFICATION" ? ["apply", "decline"] : [];
  if (d.kind === "SESSION") return d.item.status === "NEEDS_REVIEW" && !d.item.out_punch_event_id ? ["session-out"] : [];
  if (d.item.status !== "OPEN") return [];
  return d.item.type === "IN" ? ["approve-in", "dismiss"] : d.item.work_session_id ? ["approve-out", "dismiss"] : ["dismiss"];
}
const LABEL: Record<Action, string> = {
  "approve-in": "Approve clock-in", "approve-out": "Approve clock-out", dismiss: "Dismiss – duplicate/no time",
  "session-out": "Approve clock-out", apply: "Approve correction", decline: "Reject", clarify: "Request clarification",
};

export function ResolutionForm({ d, onDone }: { d: ReviewDetail; onDone: () => void }) {
  const actions = actionsFor(d);
  const [action, setAction] = useState<Action | null>(actions[0] ?? null);
  const [note, setNote] = useState("");
  const [shift, setShift] = useState<string>(d.shift_options?.[0]?.assignment_id ?? "");
  const [pin, setPin] = useState<string>(d.prefill?.payable_in_local ?? "");
  const missingOut = d.kind === "SESSION" && (d.item.review_flags ?? []).includes("MISSING_OUT") && !d.item.out_sms_received_at;
  const [pout, setPout] = useState<string>(missingOut ? "" : d.prefill?.payable_out_local ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!action) return <p data-testid="resolution-none" className="text-xs text-zinc-500">{d.item.resolution ? `Resolved: ${d.item.resolution.action} — "${d.item.resolution.note}"` : d.item.review_note ? `Reviewed: ${d.item.status} — "${d.item.review_note}"` : "No action available."}</p>;
  const submit = async () => {
    setBusy(true);
    setError(null);
    const id = d.item.id;
    const path = { "approve-in": `exceptions/${id}/approve-in`, "approve-out": `exceptions/${id}/approve-out`, dismiss: `exceptions/${id}/dismiss`,
      "session-out": `sessions/${id}/approve-out`, apply: `corrections/${id}/apply`, decline: `corrections/${id}/decline`, clarify: `corrections/${id}/clarify` }[action];
    const body: Record<string, unknown> = action === "clarify" ? { message: note } : { note };
    if (action === "approve-in") Object.assign(body, { assignment_id: shift, payable_in_local: pin || null, payable_out_local: pout || null });
    if (action === "approve-out" || action === "session-out") body.payable_out_local = pout || null;
    if (action === "apply") Object.assign(body, { payable_in_local: pin || null, payable_out_local: pout || null });
    try {
      await reviewApi.act(path, body);
      toast.success(`${LABEL[action]} saved`);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const tzLabel = `venue local · ${d.timezone}`;
  return (
    <div data-testid="resolution-form" className="space-y-3 rounded-sm border border-white/15 p-4">
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button key={a} type="button" onClick={() => setAction(a)} data-testid={`resolution-action-${a}`}
            className={`rounded-sm border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider ${action === a ? "border-signal bg-signal/10 text-white" : "border-white/15 text-zinc-400"}`}>{LABEL[a]}</button>
        ))}
      </div>
      {action === "approve-in" && (d.shift_options?.length ? (
        <select value={shift} onChange={(e) => setShift(e.target.value)} data-testid="resolution-shift-select" className="h-10 w-full rounded-sm border border-white/15 bg-surface px-3 text-xs text-white">
          {d.shift_options.map((o: { assignment_id: string; label: string }) => <option key={o.assignment_id} value={o.assignment_id}>{o.label}</option>)}
        </select>
      ) : <p data-testid="resolution-no-shift" className="text-xs text-warn">This employee has no CONFIRMED shift on that date. Create or confirm a shift first.</p>)}
      {d.open_session_id && action === "approve-in" && <p data-testid="resolution-open-session" className="text-xs text-warn">The employee has an open session. Resolve that one first.</p>}
      {(action === "approve-in" || action === "apply") && (
        <label className="block text-xs text-zinc-400">Payable clock-in ({tzLabel})
          <TextInput type="datetime-local" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="resolution-payable-in" /></label>
      )}
      {(action === "approve-in" || action === "approve-out" || action === "session-out" || action === "apply") && (
        <label className="block text-xs text-zinc-400">Payable clock-out ({tzLabel}){missingOut ? " · required, no default for a missing OUT" : action === "approve-in" ? " · optional, for a completed shift" : ""}
          <TextInput type="datetime-local" value={pout} onChange={(e) => setPout(e.target.value)} data-testid="resolution-payable-out" /></label>
      )}
      <label className="block text-xs text-zinc-400">Resolution note (required)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} data-testid="resolution-note"
          className="mt-1 w-full rounded-sm border border-white/15 bg-surface p-2 text-sm text-white" /></label>
      <ErrorText message={error} testId="resolution-error" />
      <Button onClick={submit} loading={busy} disabled={note.trim().length < 3 || (action === "approve-in" && !shift)} data-testid="resolution-submit">{LABEL[action]}</Button>
    </div>
  );
}
